import React, {useEffect, useState} from 'react';
import {Box, Grid, TextField, Typography} from '@mui/material';
import WhepVideoPlayer from '../WhepVideoPlayer';

/**
 * Pure controlled form for {@link MachineCameraUpdateDto}: two WHEP URLs +
 * a debounced live preview of each so the operator sees the new URL working
 * BEFORE saving.
 *
 * Shape: {@code { primaryUrl: string|null, secondaryUrl: string|null }}.
 *
 * The live preview is a render side-effect (it mounts a WhepVideoPlayer
 * that opens a WebRTC peer-connection to the typed URL). Debouncing avoids
 * thrashing the SRS server while the user types. NO save logic here —
 * caller persists via EditableCard.save or a Pattern-A page's onSubmit.
 */
const MachineCameraForm = ({value, onChange}) => {
    const set = (patch) => onChange({...value, ...patch});

    const debouncedPrimary = useDebouncedValue(value?.primaryUrl ?? '', 600);
    const debouncedSecondary = useDebouncedValue(value?.secondaryUrl ?? '', 600);

    return (
        <Grid container spacing={2}>
            <CameraField
                label="Primary"
                value={value?.primaryUrl ?? ''}
                onChange={(v) => set({primaryUrl: v})}
                previewUrl={debouncedPrimary}
                previewLabel="main (live preview)"
            />
            <CameraField
                label="Secondary"
                value={value?.secondaryUrl ?? ''}
                onChange={(v) => set({secondaryUrl: v})}
                previewUrl={debouncedSecondary}
                previewLabel="second (live preview)"
            />
        </Grid>
    );
};

const CameraField = ({label, value, onChange, previewUrl, previewLabel}) => (
    <Grid size={{xs: 12, md: 6}}>
        <Typography variant="subtitle2" gutterBottom>{label}</Typography>
        <TextField
            fullWidth
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://srs.host/rtc/v1/whep/?app=live&stream=..."
            sx={{fontFamily: 'monospace'}}
        />
        <Box sx={{mt: 1}}>
            <WhepVideoPlayer url={previewUrl} label={previewLabel}/>
        </Box>
    </Grid>
);

function useDebouncedValue(value, delayMs) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setV(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);
    return v;
}

export default MachineCameraForm;
